import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { AlgoliaMedia } from '@omniafishing/core';
import { Drawer, Input, Pagination } from 'antd';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { AlgoliaMediaCard } from '../../components/algolia_media_card/algolia_media_card';
import { PRODUCTS_NAV_HEIGHT_APPROX } from '../../components/products_nav/products_nav';
import { SpanLink } from '../../components/span_link/span_link';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { OmniaQueryParams } from '../../lib/query_string';
import { scrollToElementTop } from '../../lib/scroll';
import { FacetGroup } from './facet_group';
import styles from './media_list.less';
import { MediaFacets, MEDIA_PAGE_SIZE_DEFAULT } from './media_list_container';
import { OmniaButton } from '../../components/omnia_button/omnia_button';

interface MediaListProps {
  facets: Record<MediaFacets, Record<string, number>>;
  media: AlgoliaMedia[];
  mediaTotalCount: number;
  page: number;
}

export interface MediaListQueryParams {
  [OmniaQueryParams.ambassadors]: string[];
  [OmniaQueryParams.brands]: string[];
  [OmniaQueryParams.pg]: string;
  [OmniaQueryParams.species]: string[];
  [OmniaQueryParams.techniques]: string[];
  q: string;
  states: string[];
}

export const MediaList = (props: MediaListProps) => {
  const { media, facets, mediaTotalCount, page } = props;

  const { isMobile, isDesktop } = useResponsive();
  const [open, setOpen] = useState(false);
  const topRef = useRef();
  const { getCurrentQuery, replaceQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<MediaListQueryParams>();
  const { q, species, techniques, brands, ambassadors, states } = currentQuery;
  const queryTechniques = techniques || [];
  const queryBrands = brands || [];
  const queryAmbassadors = ambassadors || [];
  const queryStates = states || [];
  const querySpecies = species || [];

  const allFacets = [
    ...queryTechniques,
    ...queryBrands,
    ...queryAmbassadors,
    ...queryStates,
    ...querySpecies,
  ];
  const hasFacets = allFacets.length > 0;

  const setQueryThrottled = _.throttle(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      replaceQueryString<MediaListQueryParams>({
        q: e.target.value,
        pg: '1',
      });
    },
    500,
    {
      leading: false,
      trailing: true,
    }
  );

  const clearFilters = () =>
    replaceQueryString<MediaListQueryParams>({
      pg: '1',
      species: [],
      techniques: [],
      brands: [],
      ambassadors: [],
      states: [],
    });

  const scrollTop = () => {
    scrollToElementTop(topRef.current, PRODUCTS_NAV_HEIGHT_APPROX + 12);
  };

  const facetsContent = (
    <div className={styles.facets}>
      <FacetGroup
        facet={facets[MediaFacets.SPECIES]}
        title="Species"
        onChange={(values) => {
          replaceQueryString<MediaListQueryParams>({
            pg: '1',
            species: values,
          });
          scrollTop();
        }}
        value={querySpecies}
      />
      <FacetGroup
        facet={facets[MediaFacets.TECHNIQUES]}
        title="Techniques"
        onChange={(values) => {
          replaceQueryString<MediaListQueryParams>({
            pg: '1',
            techniques: values,
          });
          scrollTop();
        }}
        value={queryTechniques}
      />
      <FacetGroup
        facet={facets[MediaFacets.BRANDS]}
        title="Brands"
        onChange={(values) => {
          replaceQueryString<MediaListQueryParams>({
            pg: '1',
            brands: values,
          });
          scrollTop();
        }}
        value={queryBrands}
      />
      <FacetGroup
        facet={facets[MediaFacets.AMBASSADORS]}
        title="Ambassadors"
        onChange={(values) => {
          replaceQueryString<MediaListQueryParams>({
            pg: '1',
            ambassadors: values,
          });
          scrollTop();
        }}
        value={queryAmbassadors}
      />
      <FacetGroup
        facet={facets[MediaFacets.STATES]}
        title="States"
        onChange={(values) => {
          replaceQueryString<MediaListQueryParams>({
            pg: '1',
            states: values,
          });
          scrollTop();
        }}
        value={queryStates}
      />
    </div>
  );

  return (
    <section className={styles.wrapper}>
      <h1 className={styles.heading}>Videos & Articles</h1>
      <section className={styles.topBar} ref={topRef}>
        <div className={styles.searchBar}>
          <Input
            defaultValue={q}
            onChange={setQueryThrottled}
            placeholder={isMobile ? 'Search' : 'Search by Topic, Ambassador, Lake, Product, Brand'}
            prefix={<SearchOutlined className={styles.searchIcon} />}
            size="large"
            className={styles.searchInput}
          />
        </div>
        <div className={styles.filtersButtons}>
          {isMobile && (
            <OmniaButton
              kind="tertiary"
              onClick={() => {
                setOpen((o) => !open);
              }}
              size="lg"
            >
              <FilterOutlined /> Filters
            </OmniaButton>
          )}
          {isDesktop && hasFacets && (
            <OmniaButton
              kind="tertiary"
              size="lg"
              onClick={() => {
                clearFilters();
              }}
            >
              Clear Filters ({allFacets.length})
            </OmniaButton>
          )}
        </div>
      </section>
      <section className={styles.resultsCount}>{mediaTotalCount} Results</section>
      <section className={styles.results}>
        {isDesktop && facetsContent}
        <div className={styles.media}>
          {media.length > 0 ? (
            <>
              <ol className={styles.mediaCards}>
                {media.map((m) => (
                  <li key={m.uid} className={styles.mediaCard}>
                    <AlgoliaMediaCard media={m} />
                  </li>
                ))}
              </ol>
              <Pagination
                current={page}
                pageSize={MEDIA_PAGE_SIZE_DEFAULT}
                total={mediaTotalCount}
                showSizeChanger={false}
                hideOnSinglePage
                className={styles.pagination}
                onChange={(pg) => {
                  scrollTop();
                  replaceQueryString<MediaListQueryParams>({
                    pg: pg.toString(),
                  });
                }}
                itemRender={(current, type, originalElement) => {
                  if (type === 'prev') {
                    return <a rel="nofollow">Previous</a>;
                  }
                  if (type === 'next') {
                    return <a rel="nofollow">Next</a>;
                  }
                  return originalElement;
                }}
              />
            </>
          ) : (
            <>
              <p>
                No results for your search query.{' '}
                {hasFacets && (
                  <>
                    Try <SpanLink onClick={clearFilters}>clearing your filters</SpanLink> or a new
                    search term.
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </section>
      {isMobile && (
        <Drawer
          className={styles.filtersDrawer}
          open={open}
          onClose={() => setOpen(false)}
          title="Filters"
          footer={
            <div className={styles.buttonGroup}>
              <OmniaButton
                onClick={clearFilters}
                size="lg"
                kind="tertiary"
                block
                isDisabled={!hasFacets}
              >
                Clear All
              </OmniaButton>
              <OmniaButton
                onClick={() => setOpen(false)}
                kind="primary"
                size="lg"
                block
                className={styles.submitButton}
              >
                Apply
              </OmniaButton>
            </div>
          }
        >
          {facetsContent}
        </Drawer>
      )}
    </section>
  );
};
